import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, CheckSquare, Calendar, Target, TrendingUp, AlertCircle,
  ArrowRight, User, RefreshCw, ChevronDown, ChevronUp,
  Folder, MapPin, Play, Square, Coffee, Sun, Moon,
  FileText, Plus, Briefcase, Activity, Award, Zap,
  CalendarDays, Timer, TrendingDown, PartyPopper, ClipboardList,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtDateLong = (d) => d ? new Date(d).toLocaleDateString('fr-FR', {
  weekday: 'long', day: 'numeric', month: 'long'
}) : '—';
const fmtTime = (t) => t ? t.slice(0, 5) : '—';
const daysUntil = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
};
const formatHours = (h) => {
  if (h == null) return '0h00';
  const abs = Math.abs(h);
  const hrs = Math.floor(abs);
  const mins = Math.round((abs - hrs) * 60);
  const sign = h < 0 ? '-' : '';
  return `${sign}${hrs}h${mins.toString().padStart(2, '0')}`;
};
const formatHoursSigned = (h) => {
  if (h == null) return '±0h00';
  const abs = Math.abs(h);
  const hrs = Math.floor(abs);
  const mins = Math.round((abs - hrs) * 60);
  const sign = h >= 0 ? '+' : '-';
  return `${sign}${hrs}h${mins.toString().padStart(2, '0')}`;
};

// ─── Statut styles ────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  'À faire':    { bg: 'bg-elevated-1',    text: 'text-text-secondary',   dot: 'bg-gray-400' },
  'En cours':   { bg: 'bg-blue-100 dark:bg-blue-900/40',    text: 'text-blue-700 dark:text-blue-300',   dot: 'bg-blue-500' },
  'En retard':  { bg: 'bg-red-100 dark:bg-red-900/40',     text: 'text-red-700 dark:text-red-300',    dot: 'bg-red-500' },
  'Terminé':    { bg: 'bg-green-100 dark:bg-green-900/40',   text: 'text-green-700 dark:text-green-300',  dot: 'bg-green-500' },
  'Annulée':    { bg: 'bg-orange-100 dark:bg-orange-900/40',  text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-400' },
  'Impossible': { bg: 'bg-rose-100 dark:bg-rose-900/40',    text: 'text-rose-700 dark:text-rose-300',   dot: 'bg-rose-500' },
};
const getStatusStyle = (s) => STATUS_STYLES[s] || STATUS_STYLES['À faire'];

const CONGE_STATUS = {
  'en_attente': { bg: 'bg-amber-100 dark:bg-amber-900/40',  text: 'text-amber-700 dark:text-amber-300',  label: 'En attente',  icon: Clock },
  'accepte':    { bg: 'bg-green-100 dark:bg-green-900/40',  text: 'text-green-700 dark:text-green-300',  label: 'Accepté',     icon: CheckSquare },
  'refuse':     { bg: 'bg-red-100 dark:bg-red-900/40',    text: 'text-red-700 dark:text-red-300',    label: 'Refusé',      icon: AlertCircle },
};
const getCongeStyle = (s) => CONGE_STATUS[s] || CONGE_STATUS['en_attente'];

const CONGE_TYPES = {
  'conges_payes': 'Congés payés',
  'rtt': 'RTT',
  'maladie': 'Maladie',
  'sans_solde': 'Sans solde',
  'autre': 'Autre',
};

// ─── Reusable Sub-Components ──────────────────────────────────────────────────

const WidgetCard = ({ children, className = '' }) => (
  <div className={`bg-surface rounded-2xl shadow-sm border border-border-subtle overflow-hidden ${className}`}>
    {children}
  </div>
);

const WidgetHeader = ({ icon: Icon, title, count, action, color = 'text-text-primary' }) => (
  <div className="px-5 py-4 flex items-center justify-between border-b border-border-subtle">
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-elevated-1 flex items-center justify-center">
        <Icon className={`w-4.5 h-4.5 ${color}`} />
      </div>
      <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide">{title}</h3>
      {count !== undefined && (
        <span className="bg-elevated-1 text-text-secondary text-xs font-bold px-2 py-0.5 rounded-full">{count}</span>
      )}
    </div>
    {action}
  </div>
);

const EmptyState = ({ icon: Icon, message, action }) => (
  <div className="py-8 text-center">
    <Icon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
    <p className="text-sm text-text-muted">{message}</p>
    {action}
  </div>
);

const LoadingSpinner = ({ size = 'sm' }) => (
  <div className="flex justify-center py-6">
    <div className={`animate-spin rounded-full border-2 border-border-subtle border-t-blue-500 ${size === 'sm' ? 'w-6 h-6' : 'w-10 h-10'}`} />
  </div>
);

const StatusBadge = ({ status, styles }) => {
  const s = styles || getStatusStyle(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot || ''}`} />
      {status}
    </span>
  );
};

const ProgressBar = ({ pct, color = 'bg-blue-500', height = 'h-1.5' }) => (
  <div className={`w-full bg-elevated-1 rounded-full ${height} overflow-hidden`}>
    <div
      className={`${height} rounded-full transition-all duration-500 ${color}`}
      style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%` }}
    />
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// WIDGET 1 — Quick Clock-In (CTA Principal)
// ═══════════════════════════════════════════════════════════════════════════════
const QuickClockInWidget = ({ authUser, authToken, pointages, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const today = new Date().toISOString().split('T')[0];
  const empId = authUser?.employee_id || authUser?.id;
  const myPointage = pointages.filter(p => (p.user_id === empId || p.employee_id === empId) && p.date === today)
    .sort((a, b) => (a.heure_arrivee || '').localeCompare(b.heure_arrivee || ''))[0];
  const isActive = myPointage && myPointage.statut === 'en_cours';
  const isDone = myPointage && myPointage.statut === 'termine';
  const isOnBreak = isActive && myPointage?.pause_debut && !myPointage?.pause_fin;

  // Calculate duration since check-in (minus pause if applicable) — with seconds for live display
  const durationSinceCheckIn = (() => {
    if (!isActive || !myPointage?.heure_arrivee) return null;
    const [h, m] = myPointage.heure_arrivee.split(':').map(Number);
    const startSec = h * 3600 + m * 60;
    const currentSec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    let diffSec = currentSec - startSec;
    // Subtract pause duration if pause is finished
    if (myPointage.pause_debut && myPointage.pause_fin) {
      const [ph, pm] = myPointage.pause_debut.split(':').map(Number);
      const [pfh, pfm] = myPointage.pause_fin.split(':').map(Number);
      diffSec -= ((pfh * 3600 + pfm * 60) - (ph * 3600 + pm * 60));
    }
    if (diffSec < 0) return null;
    const hrs = Math.floor(diffSec / 3600);
    const mins = Math.floor((diffSec % 3600) / 60);
    const secs = diffSec % 60;
    return `${hrs}h${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  })();

  // Calculate pause duration for display
  const pauseDuration = (() => {
    if (!isOnBreak || !myPointage?.pause_debut) return null;
    const [h, m] = myPointage.pause_debut.split(':').map(Number);
    const start = h * 60 + m;
    const current = now.getHours() * 60 + now.getMinutes();
    const diff = current - start;
    if (diff < 0) return null;
    return `${Math.floor(diff / 60)}h${(diff % 60).toString().padStart(2, '0')}`;
  })();

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      const heureArrivee = now.toTimeString().slice(0, 5);
      const dateAujourdhui = now.toISOString().split('T')[0];
      await fetch(`${API_URL}/pointages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ user_id: empId, date: dateAujourdhui, heure_arrivee: heureArrivee, statut: 'en_cours', notes: '' })
      });
      onRefresh();
    } catch (err) {
      console.error('Erreur pointage arrivée:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!myPointage) return;
    setLoading(true);
    try {
      const heureDepart = now.toTimeString().slice(0, 5);
      const [arrH, arrM] = myPointage.heure_arrivee.split(':').map(Number);
      const [depH, depM] = heureDepart.split(':').map(Number);
      let totalMinutes = (depH * 60 + depM) - (arrH * 60 + arrM);
      if (totalMinutes < 0) totalMinutes += 24 * 60; // passage minuit
      // Subtract pause
      if (myPointage.pause_debut && myPointage.pause_fin) {
        const [ph, pm] = myPointage.pause_debut.split(':').map(Number);
        const [pfh, pfm] = myPointage.pause_fin.split(':').map(Number);
        totalMinutes -= ((pfh * 60 + pfm) - (ph * 60 + pm));
      }
      const totalHours = totalMinutes / 60;
      await fetch(`${API_URL}/pointages/${myPointage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ heure_depart: heureDepart, statut: 'termine', heures_travaillees: totalHours.toFixed(2) })
      });
      onRefresh();
    } catch (err) {
      console.error('Erreur pointage départ:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePauseStart = async () => {
    if (!myPointage) return;
    setLoading(true);
    try {
      const heureActuelle = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      await fetch(`${API_URL}/pointages/${myPointage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ pause_debut: heureActuelle })
      });
      onRefresh();
    } catch (err) {
      console.error('Erreur début pause:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePauseEnd = async () => {
    if (!myPointage) return;
    setLoading(true);
    try {
      const heureActuelle = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      await fetch(`${API_URL}/pointages/${myPointage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ pause_fin: heureActuelle })
      });
      onRefresh();
    } catch (err) {
      console.error('Erreur fin pause:', err);
    } finally {
      setLoading(false);
    }
  };

  // Determine background color based on state
  const bgGradient = isOnBreak
    ? 'bg-gradient-to-r from-orange-500 to-amber-600'
    : isActive
      ? 'bg-gradient-to-r from-green-500 to-emerald-600'
      : isDone
        ? 'bg-gradient-to-r from-gray-500 to-gray-600'
        : 'bg-gradient-to-r from-blue-500 to-indigo-600';

  return (
    <WidgetCard className="col-span-full">
      <div className={`p-6 ${bgGradient} text-white`}>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left: Status info */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-white/20">
              {isOnBreak ? <Coffee className="w-8 h-8" /> : isActive ? <Play className="w-8 h-8" /> : isDone ? <CheckSquare className="w-8 h-8" /> : <Clock className="w-8 h-8" />}
            </div>
            <div>
              <p className="text-white/80 text-sm font-medium">
                {now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              {/* Active: show duration as big number, current time as sub-text */}
              {isActive && !isOnBreak ? (
                <>
                  <p className="text-3xl font-bold tabular-nums">
                    {durationSinceCheckIn || '0h00'}
                  </p>
                  <p className="text-white/90 text-sm mt-0.5">
                    <span className="inline-block w-2 h-2 bg-surface rounded-full animate-pulse mr-1.5" />
                    Pointé depuis {fmtTime(myPointage.heure_arrivee)} • Il est {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    {myPointage.pause_debut && myPointage.pause_fin && (
                      <span className="ml-2 text-white/70">(pause {fmtTime(myPointage.pause_debut)}→{fmtTime(myPointage.pause_fin)})</span>
                    )}
                  </p>
                </>
              ) : isOnBreak ? (
                <>
                  <p className="text-3xl font-bold tabular-nums">
                    {pauseDuration || '0h00'}
                  </p>
                  <p className="text-white/90 text-sm mt-0.5">
                    <Coffee className="w-3.5 h-3.5 inline mr-1.5" />
                    En pause depuis {fmtTime(myPointage.pause_debut)} • Travaillé {durationSinceCheckIn}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold tabular-nums">
                    {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </p>
                  {isDone && (
                    <p className="text-white/80 text-sm mt-0.5">
                      Journée terminée • {fmtTime(myPointage.heure_arrivee)} → {fmtTime(myPointage.heure_depart)}
                      {myPointage.total_heures && ` (${formatHours(parseFloat(myPointage.total_heures))})`}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right: CTA Buttons */}
          <div className="flex gap-3 flex-wrap justify-center">
            {!isDone && !isActive && (
              <button
                onClick={handleCheckIn}
                disabled={loading}
                className="px-8 py-4 bg-surface text-blue-600 dark:text-blue-400 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-3 text-lg disabled:opacity-50"
              >
                <Play className="w-6 h-6" />
                Pointer l'arrivée
              </button>
            )}
            {isActive && !isOnBreak && (
              <>
                <button
                  onClick={handlePauseStart}
                  disabled={loading || (myPointage?.pause_debut && myPointage?.pause_fin)}
                  className="px-6 py-4 bg-white/20 hover:bg-white/30 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2.5 text-base disabled:opacity-40 disabled:cursor-not-allowed border border-white/30"
                >
                  <Coffee className="w-5 h-5" />
                  Pause
                </button>
                <button
                  onClick={handleCheckOut}
                  disabled={loading}
                  className="px-8 py-4 bg-surface text-red-600 dark:text-red-400 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-3 text-lg disabled:opacity-50"
                >
                  <Square className="w-6 h-6" />
                  Pointer le départ
                </button>
              </>
            )}
            {isOnBreak && (
              <>
                <button
                  onClick={handlePauseEnd}
                  disabled={loading}
                  className="px-8 py-4 bg-surface text-orange-600 dark:text-orange-400 font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-3 text-lg disabled:opacity-50"
                >
                  <Play className="w-6 h-6" />
                  Reprendre le travail
                </button>
              </>
            )}
            {isDone && (
              <div className="px-6 py-3 bg-white/20 rounded-xl flex items-center gap-2 text-white/90 font-semibold">
                <CheckSquare className="w-5 h-5" />
                Journée clôturée
              </div>
            )}
          </div>
        </div>
      </div>
    </WidgetCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WIDGET 2 — KPI Cards (Employee-focused)
// ═══════════════════════════════════════════════════════════════════════════════
const EmployeeKPICards = ({ myTasks, myEvents, myHours, myConges, onNavigate }) => {
  const activeTasks = myTasks.filter(t => t.status !== 'Terminé' && t.status !== 'Annulée' && t.status !== 'Annulé');
  const overdueTasks = myTasks.filter(t => {
    if (!t.due_date || t.status === 'Terminé') return false;
    return new Date(t.due_date) < new Date();
  });
  const upcomingEvents = myEvents.filter(e => new Date(e.date_debut) >= new Date());
  const pendingConges = myConges.filter(c => c.statut === 'en_attente');

  const cards = [
    {
      label: 'Tâches actives',
      value: activeTasks.length,
      sub: overdueTasks.length > 0 ? `${overdueTasks.length} en retard` : 'Tout à jour',
      icon: CheckSquare,
      gradient: 'bg-gradient-to-br from-blue-500 to-blue-600',
      alert: overdueTasks.length > 0,
      onClick: () => onNavigate('taches'),
    },
    {
      label: 'Heures cette semaine',
      value: formatHours(myHours.weekTotal),
      sub: `sur ${myHours.weekTarget}h prévues`,
      icon: Timer,
      gradient: 'bg-gradient-to-br from-indigo-500 to-purple-600',
      onClick: null,
    },
    {
      label: 'Prochain événement',
      value: upcomingEvents.length > 0 ? `J-${Math.max(0, daysUntil(upcomingEvents[0]?.date_debut))}` : '—',
      sub: upcomingEvents.length > 0 ? (upcomingEvents[0]?.nom || 'Événement').slice(0, 25) : 'Aucun planifié',
      icon: PartyPopper,
      gradient: 'bg-gradient-to-br from-orange-500 to-red-500',
      onClick: () => onNavigate('evenements'),
    },
    {
      label: 'Congés en attente',
      value: pendingConges.length,
      sub: pendingConges.length > 0 ? 'Demande(s) en cours' : 'Aucune demande',
      icon: Calendar,
      gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      onClick: () => { onNavigate('rh'); },
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, i) => (
        <div
          key={i}
          className={`relative rounded-2xl p-5 text-white shadow-lg overflow-hidden ${card.onClick ? 'cursor-pointer hover:scale-[1.03]' : ''} transition-transform ${card.gradient}`}
          onClick={card.onClick}
        >
          {card.alert && (
            <span className="absolute top-2.5 right-2.5 w-3 h-3 bg-yellow-300 rounded-full animate-pulse" />
          )}
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-white/80 text-xs font-medium uppercase tracking-wide">{card.label}</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 leading-none truncate">{card.value}</p>
              {card.sub && <p className="text-white/70 text-xs mt-1.5 truncate">{card.sub}</p>}
            </div>
            <card.icon className="w-9 h-9 text-white/25 flex-shrink-0 ml-2" />
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WIDGET 3 — Mon Planning & Tâches (badges planning + liste tâches avec check)
// ═══════════════════════════════════════════════════════════════════════════════
const MyPlanningWidget = ({ authUser, authToken, onNavigate, myTasks, propSchedules }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskStatuses, setTaskStatuses] = useState({}); // optimistic UI

  const fetchSchedules = useCallback(async () => {
    if (!authUser || !authToken) return;
    if (propSchedules && propSchedules.length > 0) {
      setSchedules(propSchedules.filter(s => s.employee_id === authUser.employee_id));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/schedules`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const all = await res.json();
        setSchedules(all.filter(s => s.employee_id === authUser.employee_id));
      }
    } catch (err) {
      console.error('Erreur chargement planning:', err);
    } finally {
      setLoading(false);
    }
  }, [authUser, authToken, propSchedules]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const todayStr = new Date().toISOString().split('T')[0];

  // Entrées planning du jour
  const todaySchedules = schedules
    .filter(s => s.date === todayStr)
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

  // Couleurs et emojis par type d'entrée
  const entryStyles = {
    'Travail':      { emoji: '💼', bg: 'bg-blue-100 dark:bg-blue-900/40',    text: 'text-blue-700 dark:text-blue-300' },
    'Télétravail':  { emoji: '🏠', bg: 'bg-cyan-100 dark:bg-cyan-900/40',    text: 'text-cyan-700 dark:text-cyan-300' },
    'Événement':    { emoji: '📅', bg: 'bg-amber-100 dark:bg-amber-900/40',   text: 'text-amber-700 dark:text-amber-300' },
    'Formation':    { emoji: '🎓', bg: 'bg-purple-100 dark:bg-purple-900/40',  text: 'text-purple-700 dark:text-purple-300' },
    'Congé':        { emoji: '🌴', bg: 'bg-yellow-100 dark:bg-yellow-900/40',  text: 'text-yellow-700 dark:text-yellow-300' },
    'Repos':        { emoji: '🏖️', bg: 'bg-orange-100 dark:bg-orange-900/40',  text: 'text-orange-700 dark:text-orange-300' },
    'Récupération': { emoji: '🔄', bg: 'bg-teal-100 dark:bg-teal-900/40',    text: 'text-teal-700 dark:text-teal-300' },
    'Dégustation':  { emoji: '🍽️', bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/40', text: 'text-fuchsia-700 dark:text-fuchsia-300' },
  };
  const getEntryStyle = (type) => entryStyles[type] || { emoji: '📝', bg: 'bg-elevated-1', text: 'text-text-secondary' };

  // Tâches du jour
  const todayTasks = (myTasks || []).filter(t => {
    if (t.status === 'Annulée') return false;
    if (t.scheduled_date && t.scheduled_start_time && t.scheduled_end_time) return t.scheduled_date === todayStr;
    const dd = t.due_date ? t.due_date.split('T')[0] : null;
    return dd === todayStr;
  }).sort((a, b) => {
    const aHasTime = a.scheduled_start_time ? 1 : 0;
    const bHasTime = b.scheduled_start_time ? 1 : 0;
    if (aHasTime !== bHasTime) return bHasTime - aHasTime;
    if (a.scheduled_start_time && b.scheduled_start_time) return a.scheduled_start_time.localeCompare(b.scheduled_start_time);
    return 0;
  });

  const scheduledTasks = todayTasks.filter(t => t.scheduled_start_time && t.scheduled_end_time);
  const classicTasks = todayTasks.filter(t => !(t.scheduled_start_time && t.scheduled_end_time));

  // Toggle checkbox (optimistic)
  const toggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'Terminé' ? 'À faire' : 'Terminé';
    setTaskStatuses(prev => ({ ...prev, [taskId]: newStatus }));
    try {
      await fetch(`${API_URL}/tasks/${taskId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ status: newStatus })
      });
    } catch {
      setTaskStatuses(prev => ({ ...prev, [taskId]: currentStatus }));
    }
  };

  const getTaskStatus = (t) => taskStatuses[t.id] || t.status;

  // Composant ligne tâche
  const TaskRow = ({ task }) => {
    const status = getTaskStatus(task);
    const isDone = status === 'Terminé';
    const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !isDone;
    const hasTime = task.scheduled_start_time && task.scheduled_end_time;
    const sStyle = getStatusStyle(status);

    return (
      <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl min-h-[44px] ${isDone ? 'bg-green-50/50' : isOverdue ? 'bg-red-50 dark:bg-red-950/40' : 'bg-elevated-1'}`}>
        <button onClick={() => toggleTask(task.id, status)}
          className={`w-6 h-6 min-w-[24px] rounded-lg border-2 flex items-center justify-center transition ${isDone ? 'bg-green-500 border-green-500 dark:border-green-500 text-white' : 'border-border-strong hover:border-blue-400 dark:border-blue-600'}`}>
          {isDone && <CheckSquare className="w-3.5 h-3.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {hasTime && (
              <span className={`text-xs font-mono font-semibold flex-shrink-0 ${isDone ? 'text-text-muted' : 'text-indigo-600 dark:text-indigo-400'}`}>
                {task.scheduled_start_time}-{task.scheduled_end_time}
              </span>
            )}
            {!hasTime && <span className="text-[10px] text-text-muted italic flex-shrink-0">journée</span>}
            <span className={`text-sm font-medium truncate ${isDone ? 'line-through text-text-muted' : isOverdue ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-text-primary'}`}>
              {task.title}
            </span>
          </div>
          {task.vehicule_nom && (
            <span className="text-[10px] text-orange-600 dark:text-orange-400 mt-0.5 block">🚛 {task.vehicule_nom}</span>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${sStyle.bg} ${sStyle.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${sStyle.dot}`} />
          {status}
        </span>
      </div>
    );
  };

  return (
    <WidgetCard>
      <WidgetHeader
        icon={CalendarDays}
        title="Mon Planning & Tâches"
        color="text-cyan-600 dark:text-cyan-400"
        action={
          <button onClick={() => onNavigate('taches')} className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-300 font-semibold flex items-center gap-1">
            Voir tout <ArrowRight className="w-3.5 h-3.5" />
          </button>
        }
      />

      {/* Zone 1 — Badges planning du jour */}
      <div className="px-4 py-3 border-b border-border-subtle">
        {loading ? <LoadingSpinner /> : todaySchedules.length === 0 ? (
          <p className="text-xs text-text-muted italic text-center">Pas de planning aujourd'hui</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {todaySchedules.map((s, i) => {
              const style = getEntryStyle(s.entry_type);
              return (
                <span key={s.id || i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
                  <span>{style.emoji}</span>
                  {s.entry_type || 'Travail'}
                  <span className="opacity-70">·</span>
                  <span className="font-normal">{fmtTime(s.start_time)}→{fmtTime(s.end_time)}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Zone 2 — Tâches avec checkbox */}
      <div className="p-3 space-y-3 max-h-[400px] overflow-y-auto">
        {/* Tâches journalières (avec horaire) */}
        {scheduledTasks.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Tâches planifiées
            </p>
            <div className="space-y-1.5">
              {scheduledTasks.map(t => <TaskRow key={t.id} task={t} />)}
            </div>
          </div>
        )}

        {/* Tâches classiques (sans horaire) */}
        {classicTasks.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-text-muted uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <CheckSquare className="w-3 h-3" /> Autres tâches du jour
            </p>
            <div className="space-y-1.5">
              {classicTasks.map(t => <TaskRow key={t.id} task={t} />)}
            </div>
          </div>
        )}

        {/* Aucune tâche */}
        {todayTasks.length === 0 && !loading && (
          <div className="py-4 text-center">
            <CheckSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-text-muted">Aucune tâche prévue aujourd'hui</p>
          </div>
        )}
      </div>
    </WidgetCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WIDGET 4 — My Hours Summary (estimées / réelles / modulation)
// ═══════════════════════════════════════════════════════════════════════════════
const MyHoursSummaryWidget = ({ authUser, authToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authUser || !authToken) return;
    const loadData = async () => {
      setLoading(true);
      const now = new Date();
      const year = now.getFullYear();
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      try {
        const res = await fetch(`${API_URL}/modulation/${authUser.id}?year=${year}&month=${month}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          setData(await res.json());
        }
      } catch (err) {
        console.error('Erreur modulation:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [authUser, authToken]);

  const heuresContrat = authUser?.heures_hebdomadaires || 35;

  return (
    <WidgetCard>
      <WidgetHeader icon={Timer} title="Mes Heures" color="text-indigo-600 dark:text-indigo-400" />
      <div className="p-5">
        {loading ? <LoadingSpinner /> : !data ? (
          <EmptyState icon={Clock} message="Données de modulation indisponibles" />
        ) : (
          <div className="space-y-4">
            {/* Monthly overview */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-xl">
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Prévues</p>
                <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{formatHours(data.heures_prevues || heuresContrat * 4.33)}</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-950/40 rounded-xl">
                <p className="text-xs text-green-600 dark:text-green-400 font-medium">Réalisées</p>
                <p className="text-xl font-bold text-green-700 dark:text-green-300">{formatHours(data.heures_reelles || 0)}</p>
              </div>
              <div className={`p-3 rounded-xl ${(data.compteur || 0) >= 0 ? 'bg-emerald-50 dark:bg-emerald-950/40' : 'bg-red-50 dark:bg-red-950/40'}`}>
                <p className={`text-xs font-medium ${(data.compteur || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>Solde</p>
                <p className={`text-xl font-bold ${(data.compteur || 0) >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                  {formatHoursSigned(data.compteur || 0)}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-xs text-text-muted mb-1.5">
                <span>Progression du mois</span>
                <span>{Math.round(((data.heures_reelles || 0) / (data.heures_prevues || heuresContrat * 4.33)) * 100)}%</span>
              </div>
              <ProgressBar
                pct={((data.heures_reelles || 0) / (data.heures_prevues || heuresContrat * 4.33)) * 100}
                color={(data.compteur || 0) >= 0 ? 'bg-emerald-500' : 'bg-amber-500'}
                height="h-2.5"
              />
            </div>

            {/* Modulation note */}
            {data.compteur !== undefined && data.compteur !== 0 && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-xs font-medium ${(data.compteur || 0) > 0 ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300'}`}>
                {(data.compteur || 0) > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {(data.compteur || 0) > 0
                  ? `Vous avez ${formatHoursSigned(data.compteur)} en avance — récupération possible`
                  : `${formatHoursSigned(data.compteur)} — heures à rattraper`
                }
              </div>
            )}

            <div className="text-xs text-text-muted text-center">
              Contrat : {heuresContrat}h / semaine • {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </div>
          </div>
        )}
      </div>
    </WidgetCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WIDGET 5 — My Tasks, Goals & Projects
// ═══════════════════════════════════════════════════════════════════════════════
const MyTasksAndGoalsWidget = ({ myTasks, myObjectifs, myProjects, onNavigate }) => {
  const [tab, setTab] = useState('taches');

  // Sort tasks: overdue first, then by due_date
  const sortedTasks = [...myTasks]
    .filter(t => t.status !== 'Terminé' && t.status !== 'Annulée' && t.status !== 'Annulé')
    .sort((a, b) => {
      const aOverdue = a.due_date && new Date(a.due_date) < new Date() ? -1 : 0;
      const bOverdue = b.due_date && new Date(b.due_date) < new Date() ? -1 : 0;
      if (aOverdue !== bOverdue) return aOverdue - bOverdue;
      return (a.due_date || '9999').localeCompare(b.due_date || '9999');
    })
    .slice(0, 8);

  const activeObjectifs = myObjectifs.filter(o => o.statut !== 'termine' && o.statut !== 'annule');
  const activeProjects = myProjects.filter(p => p.status === 'En cours' || p.status === 'Planifié');

  const tabs = [
    { id: 'taches', label: 'Tâches', count: sortedTasks.length },
    { id: 'objectifs', label: 'Objectifs', count: activeObjectifs.length },
    { id: 'projets', label: 'Projets', count: activeProjects.length },
  ];

  return (
    <WidgetCard>
      <WidgetHeader
        icon={ClipboardList}
        title="Mon Travail"
        color="text-emerald-600 dark:text-emerald-400"
        action={
          <button onClick={() => onNavigate(tab === 'projets' ? 'projets' : tab === 'objectifs' ? 'objectifs' : 'taches')}
            className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-300 font-semibold flex items-center gap-1">
            Voir tout <ArrowRight className="w-3.5 h-3.5" />
          </button>
        }
      />
      {/* Tabs */}
      <div className="flex border-b border-border-subtle">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${tab === t.id ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50/50' : 'text-text-muted hover:text-text-secondary'}`}
          >
            {t.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${tab === t.id ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300' : 'bg-elevated-1 text-text-muted'}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="p-4 max-h-72 overflow-y-auto">
        {tab === 'taches' && (
          sortedTasks.length === 0 ? (
            <EmptyState icon={CheckSquare} message="Aucune tâche active" action={
              <button onClick={() => onNavigate('taches')} className="text-xs text-blue-500 mt-2 hover:underline">Voir mes tâches →</button>
            } />
          ) : (
            <div className="space-y-2">
              {sortedTasks.map(task => {
                const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'Terminé';
                return (
                  <div key={task.id} className={`p-3 rounded-xl border ${isOverdue ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800' : 'bg-elevated-1 border-transparent'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-text-primary truncate">{task.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <StatusBadge status={isOverdue ? 'En retard' : task.status} />
                          {task.due_date && (
                            <span className={`text-xs ${isOverdue ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-text-muted'}`}>
                              {isOverdue ? `⚠ En retard (${fmtDate(task.due_date)})` : `Échéance: ${fmtDate(task.due_date)}`}
                            </span>
                          )}
                        </div>
                      </div>
                      {task.priority && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${
                          task.priority === 'Haute' || task.priority === 'Urgente' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                          task.priority === 'Moyenne' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300' :
                          'bg-elevated-1 text-text-secondary'
                        }`}>
                          {task.priority}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab === 'objectifs' && (
          activeObjectifs.length === 0 ? (
            <EmptyState icon={Target} message="Aucun objectif actif" />
          ) : (
            <div className="space-y-3">
              {activeObjectifs.slice(0, 6).map(obj => (
                <div key={obj.id} className="p-3 bg-elevated-1 rounded-xl">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-semibold text-text-primary truncate flex-1">{obj.titre}</p>
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-bold ml-2">{Math.round(obj.progression || 0)}%</span>
                  </div>
                  <ProgressBar pct={obj.progression || 0} color={obj.progression >= 100 ? 'bg-emerald-500' : obj.progression >= 50 ? 'bg-blue-500' : 'bg-amber-500'} />
                  {obj.date_fin && (
                    <p className="text-xs text-text-muted mt-1.5">Échéance: {fmtDate(obj.date_fin)}</p>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'projets' && (
          activeProjects.length === 0 ? (
            <EmptyState icon={Folder} message="Aucun projet actif" />
          ) : (
            <div className="space-y-2">
              {activeProjects.slice(0, 6).map(proj => (
                <div key={proj.id} className="flex items-center gap-3 p-3 bg-elevated-1 rounded-xl cursor-pointer hover:bg-elevated-1 transition-colors"
                     onClick={() => onNavigate('projets')}>
                  <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center flex-shrink-0">
                    <Folder className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-text-primary truncate">{proj.name}</p>
                    <p className="text-xs text-text-muted">
                      {proj.status}
                      {proj.end_date && ` • Fin: ${fmtDate(proj.end_date)}`}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-text-muted flex-shrink-0" />
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </WidgetCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WIDGET 6 — My Next Events
// ═══════════════════════════════════════════════════════════════════════════════
const MyNextEventsWidget = ({ authUser, authToken, onNavigate }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authUser || !authToken) return;
    const loadEvents = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/evenements`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const all = await res.json();
          const now = new Date();
          const future = all
            .filter(ev => {
              const evDate = new Date(ev.date_debut);
              const isParticipant = ev.participants?.some(p => p.employee_id === authUser.employee_id);
              return evDate >= now && isParticipant;
            })
            .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut))
            .slice(0, 5);
          setEvents(future);
        }
      } catch (err) {
        console.error('Erreur événements:', err);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [authUser, authToken]);

  return (
    <WidgetCard>
      <WidgetHeader
        icon={PartyPopper}
        title="Mes Événements"
        count={events.length}
        color="text-orange-600 dark:text-orange-400"
        action={
          <button onClick={() => onNavigate('evenements')} className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-300 font-semibold flex items-center gap-1">
            Voir tout <ArrowRight className="w-3.5 h-3.5" />
          </button>
        }
      />
      <div className="p-4 max-h-72 overflow-y-auto">
        {loading ? <LoadingSpinner /> : events.length === 0 ? (
          <EmptyState icon={PartyPopper} message="Aucun événement à venir" />
        ) : (
          <div className="space-y-3">
            {events.map((ev, i) => {
              const days = daysUntil(ev.date_debut);
              const isImminent = days !== null && days <= 3;
              return (
                <div key={ev.id || i} className={`p-4 rounded-xl border ${isImminent ? 'bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-800' : 'bg-elevated-1 border-transparent'}`}>
                  <div className="flex items-start gap-3">
                    {/* Date badge */}
                    <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${isImminent ? 'bg-orange-500 text-white' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'}`}>
                      <span className="text-[10px] font-bold uppercase leading-none">
                        {new Date(ev.date_debut).toLocaleDateString('fr-FR', { month: 'short' })}
                      </span>
                      <span className="text-lg font-bold leading-none mt-0.5">
                        {new Date(ev.date_debut).getDate()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-text-primary truncate">{ev.nom}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-text-muted flex-wrap">
                        {ev.lieu && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {ev.lieu}
                          </span>
                        )}
                        {ev.date_fin && (
                          <span>
                            {fmtTime(new Date(ev.date_debut).toTimeString())} → {fmtTime(new Date(ev.date_fin).toTimeString())}
                          </span>
                        )}
                      </div>
                      {ev.nombre_invites && (
                        <p className="text-xs text-text-muted mt-0.5">{ev.nombre_invites} invités</p>
                      )}
                    </div>
                    {days !== null && (
                      <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${isImminent ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' : 'bg-elevated-1 text-text-secondary'}`}>
                        J-{Math.max(0, days)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </WidgetCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WIDGET 7 — My Leave Requests + Secondary Action (demande de congé)
// ═══════════════════════════════════════════════════════════════════════════════
const MyLeaveRequestsWidget = ({ authUser, authToken, onNavigate }) => {
  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authUser || !authToken) return;
    const loadConges = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/conges`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const all = await res.json();
          setConges(all.filter(c => c.employee_id === authUser.employee_id).sort((a, b) => (b.id || 0) - (a.id || 0)));
        }
      } catch (err) {
        console.error('Erreur congés:', err);
      } finally {
        setLoading(false);
      }
    };
    loadConges();
  }, [authUser, authToken]);

  return (
    <WidgetCard>
      <WidgetHeader
        icon={Calendar}
        title="Mes Congés"
        count={conges.length}
        color="text-purple-600 dark:text-purple-400"
        action={
          <button
            onClick={() => { onNavigate('rh'); setTimeout(() => window.dispatchEvent(new Event('openCongesModal')), 300); }}
            className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors shadow-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            Demander un congé
          </button>
        }
      />
      <div className="p-4 max-h-64 overflow-y-auto">
        {loading ? <LoadingSpinner /> : conges.length === 0 ? (
          <EmptyState
            icon={Calendar}
            message="Aucune demande de congé"
            action={
              <button onClick={() => { onNavigate('rh'); setTimeout(() => window.dispatchEvent(new Event('openCongesModal')), 300); }}
                className="text-xs text-purple-500 mt-2 hover:underline">Faire une demande →</button>
            }
          />
        ) : (
          <div className="space-y-2">
            {conges.slice(0, 6).map(c => {
              const style = getCongeStyle(c.statut);
              const StatusIcon = style.icon;
              return (
                <div key={c.id} className="flex items-center gap-3 p-3 bg-elevated-1 rounded-xl">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${style.bg}`}>
                    <StatusIcon className={`w-4 h-4 ${style.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary truncate">
                      {CONGE_TYPES[c.type] || c.type}
                    </p>
                    <p className="text-xs text-text-muted">
                      {fmtDate(c.date_debut)} → {fmtDate(c.date_fin)}
                      {c.nombre_jours && ` (${c.nombre_jours}j)`}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${style.bg} ${style.text}`}>
                    {style.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </WidgetCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT — Dashboard Employé
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// WIDGET — Export Excel Heures
// ═══════════════════════════════════════════════════════════════════════════════
const MyExportHoursWidget = ({ authUser, authToken, pointages }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);

  const handleExport = () => {
    setExporting(true);
    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const monthStart = new Date(year, month - 1, 1);
      const monthEnd = new Date(year, month, 0);

      // Filtrer les pointages du mois pour cet employé (terminés)
      const monthPointages = pointages.filter(p => {
        const pDate = new Date(p.date);
        return p.user_id === authUser?.id &&
               pDate >= monthStart &&
               pDate <= monthEnd &&
               p.statut === 'termine';
      }).sort((a, b) => new Date(a.date) - new Date(b.date));

      if (monthPointages.length === 0) {
        alert('Aucun pointage terminé à exporter pour ce mois.');
        setExporting(false);
        return;
      }

      // Préparer les données pour Excel
      const excelData = monthPointages.map(p => ({
        'Date': new Date(p.date).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }),
        'Arrivée': p.heure_arrivee || '',
        'Départ': p.heure_depart || '',
        'Pause Début': p.pause_debut || '',
        'Pause Fin': p.pause_fin || '',
        'Heures Travaillées': parseFloat(p.heures_travaillees || p.total_heures || 0).toFixed(2),
        'Notes': p.notes || ''
      }));

      // Ligne de total
      const totalHours = monthPointages.reduce((sum, p) => sum + (parseFloat(p.heures_travaillees || p.total_heures) || 0), 0);
      excelData.push({
        'Date': 'TOTAL',
        'Arrivée': '',
        'Départ': '',
        'Pause Début': '',
        'Pause Fin': '',
        'Heures Travaillées': totalHours.toFixed(2),
        'Notes': `${monthPointages.length} jour(s) travaillé(s)`
      });

      // Créer le fichier Excel
      const ws = XLSX.utils.json_to_sheet(excelData);
      // Auto-width des colonnes
      const colWidths = Object.keys(excelData[0]).map(key => ({
        wch: Math.max(key.length, ...excelData.map(row => String(row[key] || '').length)) + 2
      }));
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      const monthName = new Date(year, month - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      XLSX.utils.book_append_sheet(wb, ws, `Heures ${monthName}`);

      // Télécharger
      const empName = `${authUser.first_name}_${authUser.last_name}`.replace(/\s/g, '_');
      const fileName = `mes_heures_${empName}_${year}_${month.toString().padStart(2, '0')}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('Erreur export:', err);
      alert('Erreur lors de l\'export. Veuillez réessayer.');
    } finally {
      setExporting(false);
    }
  };

  // Calculer les stats du mois sélectionné
  const [year, month] = selectedMonth.split('-').map(Number);
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  const monthPointages = pointages.filter(p => {
    const pDate = new Date(p.date);
    return p.user_id === authUser?.id &&
           pDate >= monthStart &&
           pDate <= monthEnd &&
           p.statut === 'termine';
  });
  const totalHoursMonth = monthPointages.reduce((sum, p) => sum + (parseFloat(p.heures_travaillees || p.total_heures) || 0), 0);

  return (
    <WidgetCard>
      <WidgetHeader icon={Download} title="Export Mes Heures" color="text-purple-600 dark:text-purple-400" />
      <div className="p-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-text-muted mb-1.5">Mois</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 border border-border-subtle rounded-lg text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-400 dark:border-purple-600 outline-none"
            />
          </div>
          <div className="text-center sm:text-left">
            <p className="text-xs text-text-muted mb-1">
              {monthPointages.length} jour(s) • <span className="font-semibold text-text-secondary">{totalHoursMonth.toFixed(1)}h</span>
            </p>
            <button
              onClick={handleExport}
              disabled={exporting || monthPointages.length === 0}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Export...' : 'Télécharger Excel'}
            </button>
          </div>
        </div>
      </div>
    </WidgetCard>
  );
};

const DashboardEmployee = ({ authUser, authToken, employees, pointages, tasks, schedules: propSchedules, onNavigate, onRefresh }) => {
  const [myObjectifs, setMyObjectifs] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [loadingExtra, setLoadingExtra] = useState(true);

  // Load objectives & projects assigned to this employee
  useEffect(() => {
    if (!authUser || !authToken) return;
    const loadExtra = async () => {
      setLoadingExtra(true);
      try {
        const [objRes, projRes] = await Promise.all([
          fetch(`${API_URL}/objectifs`, { headers: { 'Authorization': `Bearer ${authToken}` } }),
          fetch(`${API_URL}/projects`, { headers: { 'Authorization': `Bearer ${authToken}` } }),
        ]);
        if (objRes.ok) {
          const objs = await objRes.json();
          setMyObjectifs(objs.filter(o => o.proprietaire_id === authUser.employee_id));
        }
        if (projRes.ok) {
          const projs = await projRes.json();
          setMyProjects(projs.filter(p =>
            p.chef_de_projet_id === authUser.employee_id ||
            (p.members && p.members.some(m => m.employee_id === authUser.employee_id))
          ));
        }
      } catch (err) {
        console.error('Erreur chargement objectifs/projets:', err);
      } finally {
        setLoadingExtra(false);
      }
    };
    loadExtra();
  }, [authUser, authToken]);

  // Filter my tasks
  const myTasks = tasks.filter(t =>
    t.assigned_to === authUser?.employee_id || t.assigned_to === authUser?.id
  );

  // Calculate hours for KPI cards
  const today = new Date();
  const dayOfWeek = today.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() + mondayOffset);
  const weekStartStr = weekStart.toISOString().split('T')[0];
  const todayStr = today.toISOString().split('T')[0];

  const myWeekPointages = pointages.filter(p =>
    (p.user_id === authUser?.id) &&
    p.date >= weekStartStr &&
    p.date <= todayStr &&
    p.total_heures
  );
  const weekTotal = myWeekPointages.reduce((sum, p) => sum + parseFloat(p.total_heures || 0), 0);
  const weekTarget = authUser?.heures_hebdomadaires || 35;

  const myConges = []; // Will be loaded by the LeaveRequestsWidget internally

  return (
    <div className="space-y-5">
      {/* 1. Quick Clock-In — Full width CTA */}
      <QuickClockInWidget
        authUser={authUser}
        authToken={authToken}
        pointages={pointages}
        onRefresh={onRefresh}
      />

      {/* 2. KPI Cards */}
      <EmployeeKPICards
        myTasks={myTasks}
        myEvents={[]} // Events loaded by the MyNextEventsWidget
        myHours={{ weekTotal, weekTarget }}
        myConges={myConges}
        onNavigate={onNavigate}
      />

      {/* 3. Main grid: 2 columns on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left column */}
        <div className="space-y-5">
          <MyPlanningWidget authUser={authUser} authToken={authToken} onNavigate={onNavigate} myTasks={myTasks} propSchedules={propSchedules} />
          <MyHoursSummaryWidget authUser={authUser} authToken={authToken} />
          <MyExportHoursWidget authUser={authUser} authToken={authToken} pointages={pointages} />
        </div>

        {/* Right column */}
        <div className="space-y-5">
          <MyTasksAndGoalsWidget
            myTasks={myTasks}
            myObjectifs={myObjectifs}
            myProjects={myProjects}
            onNavigate={onNavigate}
          />
          <MyNextEventsWidget authUser={authUser} authToken={authToken} onNavigate={onNavigate} />
          <MyLeaveRequestsWidget authUser={authUser} authToken={authToken} onNavigate={onNavigate} />
        </div>
      </div>
    </div>
  );
};

export default DashboardEmployee;
